import React from 'react';
import moment from 'moment';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { formatMoney } from '../../util/currency';
import { createSlug } from '../../util/urlHelpers';
import { formatThisYearDateToShorter } from '../../util/dates';
import { NamedLink, ResponsiveImage } from '../../components';

import css from './OrderDetails.css';


const EditIcon = ({ className }) => (
  <svg
    className={className}
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const OrderDetails = ({
  providerName,
  listings = [],
  intl,
  actualOrderedProducts = [],
  transaction,
  children,
  codeMessage,
  titleMessage,
  transactionStatusAttributes = {},
  canSwapPieces = false,
  onOpenSwapModal
}) => {
  if (!transaction) return null;
  const { end, start, code } = transaction.attributes || {};
  const isInternalUse = !!transaction.attributes?.internalUse;

  return (
    <div className={css.container}>
      <div className={classNames(css.titleContainer, css.title)}>
        <p className={css.title}>
          <FormattedMessage id={titleMessage} values={{ providerName }} />
          {code && (
            <span className={css.code}>
              <FormattedMessage id={codeMessage} />
              <span className={css.codeValue}>{` ${code}`}</span>
            </span>
          )}
          {isInternalUse && (
            <span className={css.internalUseBadge}>
              Wypożyczenie wewnętrzne
            </span>
          )}
        </p>
        <div className={classNames(css.statusText, transactionStatusAttributes.class)}>
          <FormattedMessage id={transactionStatusAttributes.textStatus} />
        </div>
        {transaction.attributes?.stolen && (
          <div className={classNames(css.statusText, css.stolen)}>
            <FormattedMessage id="OrderDetails.stolenStatus" />
          </div>
        )}
      </div>
      {children}
      <div className={css.mainContent}>
        {actualOrderedProducts.map(({ listingId, lineTotal, seats }) => {
          const listing = listings.find(({ id }) => id?.uuid === listingId);
          if (!listing) return null;

          const uuid = listing.id.uuid;
          const image = listing.images?.[0];
          const { title = '', deleted } = listing.attributes || {};
          const params = { id: listingId, slug: createSlug(title) };

          const assignedPieces = listing.meta?.pieces || listing.meta?.pieceCodes || [];

          const imageContent = image && (
            <ResponsiveImage
              rootClassName={css.rootForImage}
              image={image}
              alt={title || 'product'}
              variants={["square-small", "square-small2x"]}
            />
          );

          return (
            <div
              className={classNames(css.productContainer, { [css.overlay]: !seats })}
              key={uuid || listingId}
            >
              {imageContent && (
                !deleted ? (
                  <NamedLink name="ListingPage" params={params} className={css.imageLink}>
                    {imageContent}
                  </NamedLink>
                ) : (
                  imageContent
                )
              )}

              <div className={css.productContent}>
                {!deleted ? (
                  <NamedLink name="ListingPage" params={params} className={css.productName}>
                    {title}
                  </NamedLink>
                ) : (
                  <p className={css.productName}>{title}</p>
                )}

                <div className={css.productDetails}>
                  <p className={css.text}>
                    <FormattedMessage id="DetailOrder.quantity" /> <b> {seats}</b>
                  </p>
                  <p className={css.text}>
                    <FormattedMessage id="DetailOrder.when" />{` `}
                    <b>
                      {`${formatThisYearDateToShorter(intl, moment(start))} - ${formatThisYearDateToShorter(intl, moment(end).subtract(1, 'days'))}`}
                    </b>
                  </p>
                  <p className={classNames(css.text, css.price)}>{formatMoney(intl, lineTotal)}</p>
                </div>

                {assignedPieces.length > 0 && (
                  <div className={css.piecesSection}>
                    <span className={css.piecesLabel}>
                      <FormattedMessage id="OrderDetails.assignedPieces" defaultMessage="Przypisane sztuki:" />
                    </span>
                    <div className={css.piecesList}>
                      {assignedPieces.map((piece, index) => {
                        const pieceCode = typeof piece === 'string' ? piece : piece.code || piece.id;
                        return (
                          <span key={index} className={css.pieceBadge}>
                            {pieceCode}
                            {canSwapPieces && (
                              <button
                                type="button"
                                className={css.editPieceBtn}
                                title={intl.formatMessage({
                                  id: 'OrderDetails.swapPieceTitle',
                                  defaultMessage: 'Podmień sztukę'
                                })}
                                onClick={() => onOpenSwapModal && onOpenSwapModal(piece, listing)}
                              >
                                ✏️
                              </button>
                            )}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OrderDetails;